import CallSummary from '@/karehero-app/care/CallSummary';
import ProfileScreen from '@/karehero-app/care/ProfileScreen';
import Card from '@/components/wrapped/Card';
import ActionCard from '@/components/wrapped/ActionCard';
import DocRow from '@/karehero-app/care/DocRow';
import { CALLS } from '@/karehero-app/care/fixtures';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

const FUNDING = CALLS[0]; // Upcoming, no summary yet
const ASSESSMENT = CALLS[1]; // Past, full summary + uploads + linked actions
const INITIAL = CALLS[2]; // Past, short summary, one linked action

const summary = (call: (typeof CALLS)[number]) => (
  <CallSummary
    call={call}
    onBack={noop}
    onToggleAction={noop}
    onOpenAction={noop}
  />
);

// The member-side half of Call Notes (Key Documents V0): the care circle reads
// the summary a CAM wrote against the care recipient, sees the actions and
// uploads that came out of the call, and ticks actions off. Read-only on the
// note itself. The CAM-facing note editor lives in the `calls` handover.
export const careCallsSpec: HandoverSpec = {
  id: 'care-calls',
  title: 'Call notes (member app)',
  description:
    'The Calls tab of the Care hub: upcoming and past calls, each opening into a read-only summary the CAM wrote, with the actions linked to that call and the documents uploaded against it. The family reads what was discussed and ticks off the follow-ups; the note itself can’t be edited.',
  links: [
    {
      label: 'Project: Key Documents V0 (Linear)',
      url: 'https://linear.app/karehero/project/key-documents-v0-initial-setup-29e97808f0d9',
    },
    { label: 'Live flow (Profile › Calls)', url: '/karehero-app/profile' },
    { label: 'CAM-side handover (Call notes)', url: '/handover/calls' },
    {
      label: 'Member key documents handover',
      url: '/handover/care-documents',
    },
  ],
  flows: [
    {
      title: 'Read a past call',
      steps: [
        {
          id: 'list',
          title: 'Calls list',
          tap: 'Care › Calls',
          render: () => <ProfileScreen initialTab='calls' />,
        },
        {
          id: 'open',
          title: 'Call summary',
          tap: 'Tap ‘Care assessment’ under PAST',
          render: () => summary(ASSESSMENT),
        },
      ],
    },
    {
      title: 'An upcoming call with no notes yet',
      steps: [
        {
          id: 'pending',
          title: 'Pending summary',
          tap: 'Open ‘Funding call’ (upcoming)',
          render: () => summary(FUNDING),
        },
      ],
    },
  ],
  states: [
    {
      id: 'list',
      title: 'Calls list',
      kind: 'happy',
      repro: 'Calls tab. Upcoming and past calls, grouped',
      render: () => <ProfileScreen initialTab='calls' />,
    },
    {
      id: 'summary',
      title: 'Call summary',
      kind: 'happy',
      repro: '‘Care assessment’ (past): summary, linked actions, uploads',
      render: () => summary(ASSESSMENT),
    },
    {
      id: 'pending',
      title: 'Pending summary',
      kind: 'edge',
      repro: '‘Funding call’ (upcoming): no summary yet, the cherry notice',
      render: () => summary(FUNDING),
    },
    {
      id: 'short',
      title: 'Short summary',
      kind: 'happy',
      repro: '‘Initial call’: short summary, one linked action, one upload',
      render: () => summary(INITIAL),
    },
    {
      id: 'empty',
      title: 'No calls yet',
      kind: 'empty',
      repro: 'emptyCalls prop. Before any call is booked',
      render: () => <ProfileScreen initialTab='calls' emptyCalls />,
    },
  ],
  requirements: [
    {
      text: 'Calls list: upcoming and past, grouped, each with title, date and who it was with',
      status: 'built',
      where: 'Calls tab',
    },
    {
      text: 'Open a call into a full read-only summary subscreen',
      status: 'built',
      where: 'Call summary',
    },
    {
      text: 'Call summary note is read-only for the care circle',
      status: 'built',
      where: 'Call summary',
      note: 'Carers can read but not edit or delete the note',
    },
    {
      text: 'Pending notice when the summary hasn’t been written yet',
      status: 'built',
      where: 'Funding call (upcoming)',
    },
    {
      text: 'Actions linked to the call, shown on the summary',
      status: 'built',
      where: 'Call summary › Actions linked to this call',
    },
    {
      text: 'Tick a linked action done from the summary, and open it for detail',
      status: 'built',
      where: 'Call summary',
      note: 'The note is read-only; the linked actions stay interactive',
    },
    {
      text: 'Uploads section: upload a document and list what is attached to the call',
      status: 'built',
      where: 'Call summary › Uploads',
      note: 'Upload button is a placeholder; the uploads list renders with DocRow',
    },
    {
      text: 'Empty state before any call is booked',
      status: 'built',
      where: 'No calls yet state',
    },
    {
      text: 'Book a call CTA',
      status: 'built',
      where: 'Calls tab footer',
      note: 'Placeholder action; the booking flow is not wired',
    },
    {
      text: 'New / unread badge on a call with fresh notes',
      status: 'not-built',
      note: 'No badge on the call card yet',
    },
    {
      text: 'Notification when a CAM saves new call notes',
      status: 'not-built',
      note: 'Channels are an open question (push / email / in-app)',
    },
    {
      text: 'Which CAM wrote the note (multi-CAM authorship)',
      status: 'partial',
      note: 'Cards show who the call was with; per-CAM authorship not surfaced',
    },
    {
      text: 'Error state for the call list',
      status: 'not-built',
    },
  ],
  components: {
    mui: ['Box', 'Card', 'Stack', 'Typography'],
    existing: [
      'Button (llama)',
      'PageHeader (llama)',
      'Card (wrapped)',
      'ActionCard',
      'DocRow',
      'UploadDocumentButton',
      'PushScreen',
    ],
    new: ['CallSummary', 'CallSummaryContent'],
  },
  componentsNote:
    'CallSummary is the read-only summary subscreen; CallSummaryContent is its shared body, reused when a call opens from a linked action. List rows reuse the wrapped Card (compact); linked actions reuse ActionCard and uploads reuse DocRow. The CAM-side note editor (NoteEditor, CallRow) lives in the Call notes handover.',
  componentPreviews: {
    Card: () => (
      <Card
        variant='compact'
        title='Care assessment'
        subtitle={['7th May at 10:00 · with Yasmin', 'From Yasmin']}
      />
    ),
    ActionCard: () => (
      <ActionCard
        action={ASSESSMENT.linkedActions[0]}
        onToggle={noop}
        onOpen={noop}
      />
    ),
    DocRow: () => <DocRow doc={{ id: 0, name: 'monzo_may_3months.pdf' }} />,
  },
};
